import {
	ActionIcon,
	Anchor,
	Flex,
	Group,
	Modal,
	Stack,
	Text,
} from "@mantine/core";
import {
	IconBellRinging,
	IconFingerprint,
	IconShieldCheck,
} from "@tabler/icons-react";

import ColorSchemeToggle from "@/components/ColorSchemeToggle";
import { IfImpersonating } from "@/users/components/IfImpersonating";
import { SignOutIconButton } from "@/users/components/SignOutIconButton";
import { useState } from "react";
import { Link } from "react-router";
import { ROUTES } from "@/setup/frontend/routes";
import { PushNotificationsPermissionsModal } from "@/notifications/components/PushNotificationsPermissionsModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { buildPushNotificationsPermissionsQuery } from "@/notifications/hooks/buildPushNotificationsPermissionsQuery";
import { canPromptForPushNotificationsPermissions } from "@/notifications/utils/canPromptForPushNotificationsPermissions";
import { StopImpersonatingUserButton } from "@/users/operations/stop-impersonating-user/StopImpersonatingUserButton";
import { TOTPStatus } from "@/auth/components/TOTPStatus";
import { PasskeyManagement } from "@/auth/components/PasskeyManagement";
import { useBreakpoint } from "@/common/hooks/useBreakpoint";
import { FeatureFlag } from "@/feature-flags/featureFlags";
import { useFeatureFlag } from "@/feature-flags/useFeatureFlag";

export function MainMetaSettings() {
	const queryClient = useQueryClient();
	const [pushNotificationModalOpened, setPushNotificationModalOpened] =
		useState(false);
	const [
		setupAuthenticatorAppModalOpened,
		setSetupAuthenticatorAppModalOpened,
	] = useState(false);
	const [passkeyModalOpened, setPasskeyModalOpened] = useState(false);
	const canManagePasskeys = useFeatureFlag(FeatureFlag.PasskeyManagement);

	const pushNotificationsPermissionsQuery = useQuery(
		buildPushNotificationsPermissionsQuery(),
	);
	const lessThanLg = useBreakpoint("lg");

	return (
		<Flex
			justify="flex-end"
			flex={1}
			direction="column"
			align={{ base: "center", sm: "flex-start" }}
		>
			<IfImpersonating>
				<StopImpersonatingUserButton />
			</IfImpersonating>
			{/* Bottom-aligned so the shield stays in line with the other icons when the passkey icon is stacked above it. */}
			<Group gap="xs" align="flex-end" justify="center">
				<Stack gap={0}>
					{canManagePasskeys && (
						<ActionIcon
							variant="subtle"
							size="xl"
							onClick={() => setPasskeyModalOpened(true)}
							title="Manage Passkeys"
						>
							<IconFingerprint />
						</ActionIcon>
					)}

					<ActionIcon
						variant="subtle"
						size="xl"
						onClick={() => setSetupAuthenticatorAppModalOpened(true)}
						title="Setup Authenticator App"
					>
						<IconShieldCheck />
					</ActionIcon>
				</Stack>

				<ColorSchemeToggle />

				{canPromptForPushNotificationsPermissions(
					pushNotificationsPermissionsQuery,
				) && (
					<ActionIcon
						variant="subtle"
						size="xl"
						onClick={() => setPushNotificationModalOpened(true)}
					>
						<IconBellRinging />
					</ActionIcon>
				)}

				<SignOutIconButton />

				<Text c="dimmed" component={Link} to={ROUTES.DOCS.CHANGELOG} td="none">
					{import.meta.env.VITE_APP_VERSION}
				</Text>
			</Group>

			<Modal
				fullScreen={lessThanLg}
				title="Authenticator App"
				opened={setupAuthenticatorAppModalOpened}
				onClose={() => setSetupAuthenticatorAppModalOpened(false)}
			>
				<TOTPStatus
					onRefresh={() => {
						queryClient.invalidateQueries({
							queryKey: ["userMFAStatus"],
						});
						setSetupAuthenticatorAppModalOpened(false);
					}}
				/>
			</Modal>

			{canManagePasskeys && (
				<Modal
					fullScreen={lessThanLg}
					title="Passkeys"
					opened={passkeyModalOpened}
					onClose={() => setPasskeyModalOpened(false)}
				>
					<PasskeyManagement />
				</Modal>
			)}

			{canPromptForPushNotificationsPermissions(
				pushNotificationsPermissionsQuery,
			) && (
				<PushNotificationsPermissionsModal
					opened={pushNotificationModalOpened}
					setOpened={setPushNotificationModalOpened}
				/>
			)}
		</Flex>
	);
}
